/**
 * Notification Service
 * 
 * Centralized service for sending notifications through multiple channels:
 * - In-app notifications (toasts and notification center)
 * - Email notifications
 * - Calendar invites
 * 
 * Handles routing, scheduling, and delivery of notifications based on
 * user preferences and event types.
 * 
 * Notifications are persisted to Supabase for cross-session access.
 */

import {
  getSafeSupabaseClient,
  getCurrentWorkspaceId,
} from './supabaseSync';
import { getIntegrationsApi } from './integrationsApi';
import { isNotificationStale, NOTIFICATION_STALE_DAYS } from './notificationBadgeRules';
import { NOTIFICATION_EVENT_TYPE__EVENT_TYPE } from '../generated/conceptRegisters';

// ============================================================================
// Types
// ============================================================================

/**
 * Available notification channels
 */
export type NotificationChannel = 'in_app' | 'email' | 'calendar' | 'slack' | 'sms';

/**
 * Notification event types.
 *
 * The vocabulary is owned by the notification-event-type concept register
 * (docs/3-system/00-concept-registers/process-and-session.yaml, status:
 * active) and compiled into src/generated/conceptRegisters.ts on prebuild.
 * To add or remove an event, edit the register YAML — not this file — so the
 * register and production cannot silently diverge.
 */
export const NOTIFICATION_EVENTS = NOTIFICATION_EVENT_TYPE__EVENT_TYPE;
export type NotificationEvent = (typeof NOTIFICATION_EVENT_TYPE__EVENT_TYPE)[number];

/**
 * Priority levels for notifications
 */
export type NotificationPriority = 'low' | 'normal' | 'high' | 'urgent';

/**
 * Notification payload structure
 */
export interface NotificationPayload {
  /** Unique notification ID */
  id: string;
  
  /** Event type that triggered this notification */
  event: NotificationEvent;
  
  /** Channels to deliver through */
  channels: NotificationChannel[];
  
  /** Recipient user IDs */
  recipientIds: string[];
  
  /** Notification title */
  title: string;
  
  /** Notification body/message */
  body: string;
  
  /** Priority level */
  priority: NotificationPriority;
  
  /** Event-specific data */
  data: Record<string, unknown>;
  
  /** When to send (for scheduled notifications) */
  scheduledFor?: Date;
  
  /** Deep link into the app */
  actionUrl?: string;
  
  /** Call-to-action button text */
  actionLabel?: string;
  
  /** When the notification was created */
  createdAt: Date;
  
  /** When the notification was sent (per channel) */
  sentAt?: Partial<Record<NotificationChannel, Date>>;
  
  /** Sender user ID (for mentions, assignments) */
  senderId?: string;
  /** What the notification is about (migration 864; notify-and-act FR-1). */
  subjectType?: string | null;
  subjectId?: string | null;
}

/**
 * User notification preferences
 */
export interface NotificationPreferences {
  userId: string;
  
  /** Global enabled/disabled */
  enabled: boolean;
  
  /** Per-event channel preferences */
  eventPreferences: Partial<Record<NotificationEvent, NotificationChannel[]>>;
  
  /** Quiet hours (don't send during these times) */
  quietHours?: {
    enabled: boolean;
    startTime: string; // HH:MM format
    endTime: string;
    timezone: string;
    excludeUrgent: boolean; // Still send urgent during quiet hours
  };
  
  /** Email digest preferences */
  emailDigest?: {
    enabled: boolean;
    frequency: 'immediate' | 'hourly' | 'daily' | 'weekly';
  };
}

/**
 * Stored notification (for notification center)
 */
export interface StoredNotification {
  id: string;
  userId: string;
  payload: NotificationPayload;
  read: boolean;
  readAt?: Date;
  dismissed: boolean;
  dismissedAt?: Date;
  createdAt: Date;
  /** The ask's subject moved on (1009), e.g. the finding it named was replaced (1037). */
  finishedAt?: Date;
}

// ============================================================================
// Default Event-to-Channel Mappings
// ============================================================================

/**
 * Default channels for each event type
 */
export const DEFAULT_EVENT_CHANNELS: Record<NotificationEvent, NotificationChannel[]> = {
  // Session events
  session_scheduled: ['in_app', 'email', 'calendar'],
  session_updated: ['in_app', 'email'],
  session_cancelled: ['in_app', 'email'],
  session_starting_soon: ['in_app'],
  session_started: ['in_app'],
  session_completed: ['in_app'],
  session_results_published: ['in_app', 'email'],
  
  // Cycle events
  cycle_created: ['in_app', 'email'],
  cycle_phase_changed: ['in_app', 'email'],
  cycle_completed: ['in_app', 'email'],
  cycle_priorities_published: ['in_app', 'email'],
  
  // Task events
  task_assigned: ['in_app', 'email', 'slack'],
  task_reminder: ['in_app', 'email'],
  task_closing_soon: ['in_app', 'email'],
  task_closed: ['in_app'],
  task_results_ready: ['in_app', 'email'],
  
  // Meeting events
  meeting_scheduled: ['in_app', 'email', 'calendar'],
  meeting_updated: ['in_app', 'email'],
  meeting_cancelled: ['in_app', 'email'],
  meeting_reminder: ['in_app'],
  
  // Item events
  steward_assigned: ['in_app'],
  item_state_changed: ['in_app'],
  watched_item_changed: ['in_app'],
  followed_record_changed: ['in_app'],
  item_ready_for_ranking: ['in_app'],
  item_deprioritized: ['in_app', 'email'],

  // Catchball events
  catchball_ball_tossed_to_middle: ['in_app', 'email'],
  catchball_ball_tossed_to_bottom: ['in_app', 'email'],
  catchball_ball_returned: ['in_app', 'email'],
  catchball_level_involved: ['in_app'],
  catchball_proposal_submitted: ['in_app', 'email'],
  catchball_proposal_accepted: ['in_app', 'email'],
  catchball_proposal_returned: ['in_app', 'email'],
  catchball_proposal_rejected: ['in_app', 'email'],

  // Server-side events (migration 737). These rows are written by Postgres
  // triggers, not by this service — the channel map exists so the vocabulary
  // stays exhaustive and so a client sender for one of them would route
  // sensibly. In-app only: the trigger has no email lane.
  catchball_ball_received: ['in_app'],
  catchball_ball_claimed: ['in_app'],
  catchball_ball_released: ['in_app'],
  catchball_loop_closed: ['in_app'],
  catchball_another_round: ['in_app'],
  // Ambient job catchball_overdue (migration 880) — written by the database.
  catchball_ball_overdue: ['in_app'],
  catchball_ball_overdue_sender: ['in_app'],
  // V2MOM process (migration 945)
  v2mom_asked: ['in_app', 'email'],
  v2mom_ask_withdrawn: ['in_app'],
  v2mom_submitted: ['in_app', 'email'],
  v2mom_submission_withdrawn: ['in_app'],
  v2mom_approved: ['in_app', 'email'],
  v2mom_returned: ['in_app', 'email'],
  v2mom_published: ['in_app'],
  v2mom_reopen_requested: ['in_app', 'email'],
  v2mom_reopened: ['in_app'],
  v2mom_reopen_declined: ['in_app'],
  v2mom_ask_overdue: ['in_app'],
  v2mom_ask_overdue_asker: ['in_app'],
  participation_requested: ['in_app'],
  participation_responded: ['in_app'],
  track_created: ['in_app'],
  track_owner_assigned: ['in_app'],
  participation_withdrawn: ['in_app'],
  plan_of_record_authorized: ['in_app'],
  structure_reorg_authorized: ['in_app'],
  item_pool_published: ['in_app'],
  idea_submitted: ['in_app'],
  condition_broken: ['in_app'],
  commitment_term_tripped: ['in_app'],
  decision_right_delegated: ['in_app'],

  // Server-side events (migration 745). Same rationale as the 737 block
  // above — trigger-written, in-app only.
  track_lock_engaged: ['in_app'],
  track_lock_released: ['in_app'],
  track_bound_to_cycle: ['in_app'],
  session_linked_to_track: ['in_app'],
  invitation_sent: ['in_app'],
  invitation_accepted: ['in_app'],
  resource_claim_declared: ['in_app'],
  resource_claim_withdrawn: ['in_app'],
  plan_item_placed: ['in_app'],
  plan_item_removed: ['in_app'],
  decision_recorded: ['in_app'],
  commitment_created: ['in_app'],
  commitment_status_changed: ['in_app'],
  signal_event_classified: ['in_app'],
  signal_event_confirmed: ['in_app'],
  signal_event_dismissed: ['in_app'],
  signal_verdict_recorded: ['in_app'],
  signal_event_reopened: ['in_app'],
  agent_finding_accepted: ['in_app'],
  twins_cluster_item_closed: ['in_app'],
  twins_cluster_accepted: ['in_app'],

  // 750's cycle_launched (trigger-written) was never added here.
  cycle_launched: ['in_app'],
  // Server-side events (migration 758) — trigger-written, in-app only.
  session_created: ['in_app'],
  session_invited: ['in_app'],
  plan_version_created: ['in_app'],
  org_structure_edited: ['in_app'],
  // Server-side events (migration 768) — trigger-written, in-app only.
  session_rank_overridden: ['in_app'],
  group_scores_withdrawn: ['in_app'],

  // General
  reminder: ['in_app'],
  mention: ['in_app', 'email'],
  comment_reply: ['in_app'],
  assignment: ['in_app', 'email'],
  custom: ['in_app'],
};

// ============================================================================
// Notification Service
// ============================================================================

const TABLE_NAME = 'notifications';

/** One "Live now" per person per session inside this window. */
const SESSION_LIVE_DEDUPE_HOURS = 4;

/**
 * In-memory cache for notifications (backed by Supabase)
 */
let notificationStore: StoredNotification[] = [];
const notificationListeners: ((notification: StoredNotification) => void)[] = [];

// ============================================================================
// Supabase Persistence
// ============================================================================

/**
 * Insert notification into Supabase
 */
async function insertNotificationToSupabase(
  notification: StoredNotification,
  cycleId?: string,
  taskId?: string,
  sessionId?: string
): Promise<void> {
  const supabase = getSafeSupabaseClient();
  if (!supabase) return;

  try {
    const dbRecord = {
      id: notification.id,
      user_id: notification.userId,
      event: notification.payload.event,
      title: notification.payload.title,
      body: notification.payload.body,
      priority: notification.payload.priority,
      data: notification.payload.data,
      action_url: notification.payload.actionUrl,
      action_label: notification.payload.actionLabel,
      sender_id: notification.payload.senderId,
      read: notification.read,
      dismissed: notification.dismissed,
      created_at: notification.createdAt.toISOString(),
      cycle_id: cycleId || (notification.payload.data as Record<string, unknown>)?.cycleId,
      task_id: taskId || (notification.payload.data as Record<string, unknown>)?.taskId,
      session_id: sessionId,
      workspace_id: getCurrentWorkspaceId(),
    };

    const { error } = await supabase.from(TABLE_NAME).insert(dbRecord);
    if (error) {
      console.error('[NotificationService] Supabase insert error:', error);
    } else {
      console.log('[NotificationService] Notification saved to Supabase:', notification.id);
    }
  } catch (err) {
    console.error('[NotificationService] Supabase insert exception:', err);
  }
}

/**
 * Load notifications for a user from Supabase
 */
export async function loadNotificationsForUser(userId: string): Promise<StoredNotification[]> {
  const supabase = getSafeSupabaseClient();
  if (!supabase) {
    console.log('[NotificationService] Supabase not available, using in-memory cache');
    return notificationStore.filter(n => n.userId === userId);
  }

  try {
    const query = supabase
      .from(TABLE_NAME)
      .select('*')
      .eq('user_id', userId)
      .eq('dismissed', false)
      .order('created_at', { ascending: false })
      .limit(50);

    const { data, error } = await query;

    if (error) {
      console.error('[NotificationService] Supabase load error:', error);
      return [];
    }

    const notifications: StoredNotification[] = (data || []).map(row => ({
      id: row.id,
      userId: row.user_id,
      payload: {
        id: row.id,
        event: row.event,
        channels: ['in_app'],
        recipientIds: [row.user_id],
        title: row.title,
        body: row.body,
        priority: row.priority || 'normal',
        data: row.data || {},
        actionUrl: row.action_url,
        actionLabel: row.action_label,
        senderId: row.sender_id,
        subjectType: row.subject_type ?? null,
        subjectId: row.subject_id ?? null,
        createdAt: new Date(row.created_at),
      },
      read: row.read,
      readAt: row.read_at ? new Date(row.read_at) : undefined,
      dismissed: row.dismissed,
      dismissedAt: row.dismissed_at ? new Date(row.dismissed_at) : undefined,
      createdAt: new Date(row.created_at),
      finishedAt: row.finished_at ? new Date(row.finished_at) : undefined,
    }));

    // Stale unread notifications should not keep pinging — mark read quietly,
    // through the recipient-scoped RPC (migration 820), never a direct PATCH.
    const staleUnreadIds = notifications
      .filter((n) => !n.read && isNotificationStale(n.createdAt))
      .map((n) => n.id);
    if (staleUnreadIds.length > 0) {
      const nowIso = new Date().toISOString();
      const { error: staleError } = await supabase.rpc('mark_stale_notifications_read', {
        p_days: NOTIFICATION_STALE_DAYS,
      });
      if (!staleError) {
        for (const n of notifications) {
          if (staleUnreadIds.includes(n.id)) {
            n.read = true;
            n.readAt = new Date(nowIso);
          }
        }
      }
    }

    // Update in-memory cache
    notificationStore = [
      ...notificationStore.filter(n => n.userId !== userId),
      ...notifications,
    ];

    console.log(`[NotificationService] Loaded ${notifications.length} notifications for user ${userId}`);
    return notifications;
  } catch (err) {
    console.error('[NotificationService] Supabase load exception:', err);
    return [];
  }
}

/**
 * Load dismissed notifications (history) for a user.
 * Active inbox stays on loadNotificationsForUser (dismissed=false).
 */
export async function loadNotificationHistoryForUser(
  userId: string,
  options?: { limit?: number },
): Promise<StoredNotification[]> {
  const supabase = getSafeSupabaseClient();
  const limit = options?.limit ?? 50;
  if (!supabase) {
    return notificationStore
      .filter((n) => n.userId === userId && n.dismissed)
      .sort((a, b) => b.createdAt.getTime() - a.createdAt.getTime())
      .slice(0, limit);
  }

  try {
    const { data, error } = await supabase
      .from(TABLE_NAME)
      .select('*')
      .eq('user_id', userId)
      .eq('dismissed', true)
      .order('dismissed_at', { ascending: false })
      .limit(limit);

    if (error) {
      console.error('[NotificationService] History load error:', error);
      return [];
    }

    return (data || []).map((row) => ({
      id: row.id,
      userId: row.user_id,
      payload: {
        id: row.id,
        event: row.event,
        channels: ['in_app'] as NotificationChannel[],
        recipientIds: [row.user_id],
        title: row.title,
        body: row.body,
        priority: row.priority || 'normal',
        data: row.data || {},
        actionUrl: row.action_url,
        actionLabel: row.action_label,
        senderId: row.sender_id,
        subjectType: row.subject_type ?? null,
        subjectId: row.subject_id ?? null,
        createdAt: new Date(row.created_at),
      },
      read: row.read,
      readAt: row.read_at ? new Date(row.read_at) : undefined,
      dismissed: row.dismissed,
      dismissedAt: row.dismissed_at ? new Date(row.dismissed_at) : undefined,
      createdAt: new Date(row.created_at),
    }));
  } catch (err) {
    console.error('[NotificationService] History load exception:', err);
    return [];
  }
}

/**
 * Which of `userIds` already hold a matching notification for this session in
 * the window. One round trip for the whole fan-out — the per-user
 * `hasRecentSessionNotification` below is the single-recipient case of it.
 */
export async function recipientsWithRecentSessionNotification(params: {
  userIds: string[];
  event: NotificationEvent;
  sessionId: string;
  withinHours?: number;
}): Promise<Set<string>> {
  const userIds = Array.from(new Set(params.userIds.filter(Boolean)));
  if (userIds.length === 0) return new Set();

  const supabase = getSafeSupabaseClient();
  const withinHours = params.withinHours ?? 24;
  const since = new Date(Date.now() - withinHours * 60 * 60 * 1000).toISOString();

  if (!supabase) {
    return new Set(
      notificationStore
        .filter(
          (n) =>
            userIds.includes(n.userId) &&
            n.payload.event === params.event &&
            (n.payload.data as { sessionId?: string } | undefined)?.sessionId === params.sessionId &&
            n.createdAt.getTime() >= Date.parse(since),
        )
        .map((n) => n.userId),
    );
  }

  try {
    const { data, error } = await supabase
      .from(TABLE_NAME)
      .select('user_id')
      .in('user_id', userIds)
      .eq('event', params.event)
      .eq('session_id', params.sessionId)
      .gte('created_at', since);

    if (error) {
      console.error('[NotificationService] recipientsWithRecentSessionNotification error:', error);
      return new Set();
    }
    return new Set((data ?? []).map((row) => row.user_id as string));
  } catch (err) {
    console.error('[NotificationService] recipientsWithRecentSessionNotification exception:', err);
    return new Set();
  }
}

/**
 * Dedupe helper for session reminders — true if a matching row exists in the last 24h.
 */
export async function hasRecentSessionNotification(params: {
  userId: string;
  event: NotificationEvent;
  sessionId: string;
  withinHours?: number;
}): Promise<boolean> {
  const supabase = getSafeSupabaseClient();
  const withinHours = params.withinHours ?? 24;
  const since = new Date(Date.now() - withinHours * 60 * 60 * 1000).toISOString();

  if (!supabase) {
    return notificationStore.some(
      (n) =>
        n.userId === params.userId &&
        n.payload.event === params.event &&
        (n.payload.data as { sessionId?: string } | undefined)?.sessionId === params.sessionId &&
        n.createdAt.getTime() >= Date.parse(since),
    );
  }

  try {
    const { data, error } = await supabase
      .from(TABLE_NAME)
      .select('id')
      .eq('user_id', params.userId)
      .eq('event', params.event)
      .eq('session_id', params.sessionId)
      .gte('created_at', since)
      .limit(1);

    if (error) {
      console.error('[NotificationService] hasRecentSessionNotification error:', error);
      return false;
    }
    return (data?.length ?? 0) > 0;
  } catch (err) {
    console.error('[NotificationService] hasRecentSessionNotification exception:', err);
    return false;
  }
}

/**
 * Mark notification as read in Supabase
 */
export async function markNotificationReadInSupabase(notificationId: string): Promise<boolean> {
  const supabase = getSafeSupabaseClient();
  if (!supabase) return false;

  try {
    // 703: go through the RPC, not a bare PATCH. `notifications` carries a
    // `true`-qualified UPDATE policy, so an `.eq('id', ...)`-only filter lets a
    // caller mark somebody else's notification read. The RPC is SECURITY
    // DEFINER and scopes the write to auth.uid() itself.
    const { error } = await supabase.rpc('mark_notification_read', {
      p_notification_id: notificationId,
    });

    if (error) {
      console.error('[NotificationService] Mark read error:', error);
      return false;
    }

    // Update in-memory cache
    notificationStore = notificationStore.map(n =>
      n.id === notificationId ? { ...n, read: true, readAt: new Date() } : n
    );

    return true;
  } catch (err) {
    console.error('[NotificationService] Mark read exception:', err);
    return false;
  }
}

/**
 * Mark every one of the current user's notifications as read in one round
 * trip, via the `mark_all_notifications_read` RPC (SECURITY DEFINER, scoped
 * to auth.uid() — same reasoning as markNotificationReadInSupabase above).
 * Returns the number of rows the RPC updated, or null on failure.
 */
/**
 * Mark the caller's listed notifications read: the rows that were on screen,
 * nothing more (migration 820, mark_notifications_seen). Returns the number
 * updated, or null when the write did not land.
 */
export async function markNotificationsSeenInSupabase(ids: string[]): Promise<number | null> {
  const supabase = getSafeSupabaseClient();
  if (!supabase || ids.length === 0) return 0;
  const { data, error } = await supabase.rpc('mark_notifications_seen', { p_ids: ids });
  if (error) {
    console.error('[NotificationService] mark_notifications_seen failed:', error);
    return null;
  }
  for (const n of notificationStore) {
    if (ids.includes(n.id) && !n.read) {
      n.read = true;
      n.readAt = new Date();
    }
  }
  return typeof data === 'number' ? data : ids.length;
}

export async function markAllNotificationsReadInSupabase(): Promise<number | null> {
  const supabase = getSafeSupabaseClient();
  if (!supabase) return null;

  try {
    const { data, error } = await supabase.rpc('mark_all_notifications_read');

    if (error) {
      console.error('[NotificationService] Mark all read error:', error);
      return null;
    }

    // Update in-memory cache
    notificationStore = notificationStore.map(n => (n.read ? n : { ...n, read: true, readAt: new Date() }));

    return data ?? 0;
  } catch (err) {
    console.error('[NotificationService] Mark all read exception:', err);
    return null;
  }
}

/**
 * Dismiss notification in Supabase
 */
export async function dismissNotificationInSupabase(notificationId: string): Promise<boolean> {
  const supabase = getSafeSupabaseClient();
  if (!supabase) {
    notificationStore = notificationStore.filter((n) => n.id !== notificationId);
    return true;
  }

  try {
    // 703: same reasoning as markNotificationReadInSupabase — the RPC scopes
    // the write to auth.uid(); a bare PATCH filtered on id alone does not.
    const { error } = await supabase.rpc('dismiss_notification', {
      p_notification_id: notificationId,
    });

    if (error) {
      console.error('[NotificationService] Dismiss error:', error);
      return false;
    }

    // Update in-memory cache
    notificationStore = notificationStore.filter(n => n.id !== notificationId);

    return true;
  } catch (err) {
    console.error('[NotificationService] Dismiss exception:', err);
    return false;
  }
}

/**
 * Get unread notification count for a user
 */
export async function getUnreadCountForUser(userId: string): Promise<number> {
  const supabase = getSafeSupabaseClient();
  if (!supabase) {
    return notificationStore.filter(n => n.userId === userId && !n.read).length;
  }

  try {
    const { count, error } = await supabase
      .from(TABLE_NAME)
      .select('*', { count: 'exact', head: true })
      .eq('user_id', userId)
      .eq('read', false)
      .eq('dismissed', false);

    if (error) {
      console.error('[NotificationService] Count error:', error);
      return 0;
    }

    return count || 0;
  } catch (err) {
    console.error('[NotificationService] Count exception:', err);
    return 0;
  }
}

/**
 * Send a notification through specified channels
 */
export async function sendNotification(
  payload: Omit<NotificationPayload, 'id' | 'createdAt'>
): Promise<NotificationPayload> {
  const notification: NotificationPayload = {
    ...payload,
    id: crypto.randomUUID(),
    createdAt: new Date(),
    sentAt: {},
  };
  
  // Route to each channel
  for (const channel of payload.channels) {
    try {
      switch (channel) {
        case 'in_app':
          await sendInAppNotification(notification);
          notification.sentAt!.in_app = new Date();
          break;
        case 'slack':
          await sendSlackNotification(notification);
          notification.sentAt!.slack = new Date();
          break;
        // Email, calendar and text are not sent from the browser, so nothing
        // here claims they were (PRD-tasks-asks-and-my-work FR-0a; the server
        // delivers them in roadmap E-4).
        case 'email':
        case 'calendar':
        case 'sms':
          break;
      }
    } catch (error) {
      console.error(`Failed to send ${channel} notification:`, error);
    }
  }
  
  return notification;
}

/**
 * Send in-app notification (stores in notification center)
 */
async function sendInAppNotification(payload: NotificationPayload): Promise<void> {
  const data = payload.data as Record<string, unknown> | undefined;
  const sessionId = typeof data?.sessionId === 'string' ? data.sessionId : undefined;
  const cycleId = typeof data?.cycleId === 'string' ? data.cycleId : undefined;
  const taskId = typeof data?.taskId === 'string' ? data.taskId : undefined;

  for (const userId of payload.recipientIds) {
    const stored: StoredNotification = {
      id: crypto.randomUUID(),
      userId,
      payload,
      read: false,
      dismissed: false,
      createdAt: new Date(),
    };
    
    // Add to in-memory cache
    notificationStore.push(stored);
    
    // Persist to Supabase (fire and forget) — pass FK ids so session/cycle/task link
    insertNotificationToSupabase(stored, cycleId, taskId, sessionId);
    
    // Notify listeners
    notificationListeners.forEach(listener => listener(stored));
  }
}

/**
 * Send Slack notification via the integrations API.
 * Uses publishEventToSlack so it respects channel configurations already set up in Slack settings.
 */
async function sendSlackNotification(payload: NotificationPayload): Promise<void> {
  try {
    const workspaceId = getCurrentWorkspaceId();
    const api = getIntegrationsApi(workspaceId ?? 'default');
    const recipientId = payload.recipientIds[0];
    const status = await api.getSlackStatus(recipientId);
    if (!status.connected) {
      console.log('[NotificationService] Slack not connected — skipping Slack notification');
      return;
    }
    if (!status.notifyChannelId) {
      console.log(
        '[NotificationService] No Slack notify channel (personal or workspace) — skipping',
      );
      return;
    }
    try {
      await api.notifySlack({
        userId: recipientId,
        subject: payload.title,
        message: [
          payload.body,
          payload.actionUrl
            ? `<${payload.actionUrl}|${payload.actionLabel || 'Open in Priorities.ai'}>`
            : null,
        ]
          .filter(Boolean)
          .join('\n'),
      });
    } catch {
      await api.publishEventToSlack('task_assigned', {
        title: payload.title,
        body: payload.body,
        recipientIds: payload.recipientIds,
        senderId: payload.senderId,
        actionUrl: payload.actionUrl,
        actionLabel: payload.actionLabel,
        priority: payload.priority,
        ...payload.data,
      });
    }
  } catch (err) {
    console.warn('[NotificationService] Slack notification failed (non-blocking):', err);
  }
}

// ============================================================================
// Query Functions
// ============================================================================

/**
 * Get all notifications for a user
 */
export function getNotificationsForUser(
  userId: string,
  options?: {
    unreadOnly?: boolean;
    limit?: number;
    offset?: number;
  }
): StoredNotification[] {
  let results = notificationStore.filter(n => 
    n.userId === userId && !n.dismissed
  );
  
  if (options?.unreadOnly) {
    results = results.filter(n => !n.read);
  }
  
  // Sort by creation date, newest first
  results.sort((a, b) => b.createdAt.getTime() - a.createdAt.getTime());
  
  if (options?.offset) {
    results = results.slice(options.offset);
  }
  
  if (options?.limit) {
    results = results.slice(0, options.limit);
  }
  
  return results;
}

/**
 * Get unread count for a user
 */
export function getUnreadCount(userId: string): number {
  return notificationStore.filter(n => 
    n.userId === userId && !n.read && !n.dismissed
  ).length;
}

/**
 * Mark notification as read
 */
export function markAsRead(notificationId: string): void {
  const notification = notificationStore.find(n => n.id === notificationId);
  if (notification) {
    notification.read = true;
    notification.readAt = new Date();
  }
}

/**
 * Subscribe to new notifications
 */
export function subscribeToNotifications(
  callback: (notification: StoredNotification) => void
): () => void {
  notificationListeners.push(callback);
  
  // Return unsubscribe function
  return () => {
    const index = notificationListeners.indexOf(callback);
    if (index > -1) {
      notificationListeners.splice(index, 1);
    }
  };
}

// ============================================================================
// Helper Functions for Common Notification Types
// ============================================================================

/**
 * Send session scheduled notification
 */
export async function notifySessionScheduled(
  sessionId: string,
  sessionTitle: string,
  scheduledStart: Date,
  recipientIds: string[],
  facilitatorId: string
): Promise<NotificationPayload> {
  return sendNotification({
    event: 'session_scheduled',
    channels: DEFAULT_EVENT_CHANNELS.session_scheduled,
    recipientIds,
    title: 'Session Scheduled',
    body: `"${sessionTitle}" has been scheduled for ${formatDateTime(scheduledStart)}`,
    priority: 'normal',
    data: {
      sessionId,
      sessionTitle,
      scheduledStart,
      facilitatorId,
    },
    actionUrl: `/prioritization/sessions/${sessionId}`,
    actionLabel: 'View Session',
  });
}

/**
 * Send session starting soon reminder
 */
export async function notifySessionStartingSoon(
  sessionId: string,
  sessionTitle: string,
  startsIn: number, // minutes
  recipientIds: string[]
): Promise<NotificationPayload> {
  return sendNotification({
    event: 'session_starting_soon',
    channels: DEFAULT_EVENT_CHANNELS.session_starting_soon,
    recipientIds,
    title: 'Session Starting Soon',
    body: `"${sessionTitle}" starts in ${startsIn} minutes`,
    priority: startsIn <= 5 ? 'high' : 'normal',
    data: {
      sessionId,
      sessionTitle,
      startsInMinutes: startsIn,
    },
    actionUrl: `/prioritization/sessions/${sessionId}/participate`,
    actionLabel: 'Join',
  });
}

/**
 * Whether a session can still be joined, from its row state. `session_started`
 * is the only `urgent` notification the product sends, so what it points at has
 * to be checked rather than assumed.
 */
export type SessionJoinability = 'live' | 'not_started' | 'ended' | 'unknown';

const JOINABILITY_BY_STATE: Record<string, SessionJoinability> = {
  DRAFT: 'not_started',
  CRITERIA_FINALIZED: 'live',
  RUN_COMPLETE: 'ended',
  PUBLISHED: 'ended',
};

/**
 * Resolve the current joinability of the given sessions in one round trip.
 * Ids with no row (deleted, or a demo reset) come back `unknown` — the caller
 * shows the notification, it just doesn't promise a live room.
 */
export async function loadSessionJoinability(
  sessionIds: string[],
): Promise<Record<string, SessionJoinability>> {
  const ids = Array.from(new Set(sessionIds.filter(Boolean)));
  if (ids.length === 0) return {};
  const supabase = getSafeSupabaseClient();
  if (!supabase) return {};

  try {
    const { data, error } = await supabase
      .from('prioritization_sessions')
      .select('id, state')
      .in('id', ids);

    if (error) {
      console.error('[NotificationService] loadSessionJoinability error:', error);
      return {};
    }

    const out: Record<string, SessionJoinability> = {};
    for (const row of data ?? []) {
      out[row.id as string] = JOINABILITY_BY_STATE[row.state as string] ?? 'unknown';
    }
    return out;
  } catch (err) {
    console.error('[NotificationService] loadSessionJoinability exception:', err);
    return {};
  }
}

/**
 * GAP-4 / brief-X §5 — session go-live: "Session ready: [name] · Join now"
 * Deep-links participants to the C Mode participate surface (not the facilitator /live board).
 *
 * Refuses to send unless the session is actually joinable. This is the product's
 * only `urgent` notification — the one meant to interrupt someone — and in the
 * FBD workspace not a single `session_started` row pointed at a live session:
 * the Event Designer's go-live never moved the row out of `DRAFT`, and
 * re-entering a completed step fired the whole fan-out again at a `RUN_COMPLETE`
 * session 16 minutes after it finished (prod, 2026-09-01). A "Live now · Join
 * now" alert for a room that never opened or already closed is worse than no
 * alert: it spends the one interrupt the product has on a dead end.
 */
export async function notifySessionLiveNow(params: {
  sessionId: string;
  sessionName: string;
  recipientIds: string[];
  cycleId?: string | null;
  cycleName?: string | null;
  senderId?: string;
  /** Skip the live-state check — only for callers that just transitioned the
   *  row themselves and would otherwise race their own write. */
  assumeLive?: boolean;
}): Promise<NotificationPayload | null> {
  const recipientIds = params.recipientIds.filter(Boolean);
  if (recipientIds.length === 0) return null;

  if (!params.assumeLive) {
    const joinability = (await loadSessionJoinability([params.sessionId]))[params.sessionId];
    if (joinability === 'not_started' || joinability === 'ended') {
      console.warn(
        `[NotificationService] suppressed "Live now" for session ${params.sessionId}: ${joinability}`,
      );
      return null;
    }
  }

  // Re-entering a collaborative step re-runs the whole invite path, so the same
  // room can be announced to the same person several times over. One urgent
  // alert per person per session per window is the promise.
  const alreadyTold = await recipientsWithRecentSessionNotification({
    userIds: recipientIds,
    event: 'session_started',
    sessionId: params.sessionId,
    withinHours: SESSION_LIVE_DEDUPE_HOURS,
  });
  const freshRecipients = recipientIds.filter((id) => !alreadyTold.has(id));
  if (freshRecipients.length === 0) return null;

  const cycleSuffix = params.cycleName ? ` · ${params.cycleName}` : '';
  return sendNotification({
    event: 'session_started',
    channels: DEFAULT_EVENT_CHANNELS.session_started ?? ['in_app'],
    recipientIds: freshRecipients,
    title: 'Live now',
    body: `Session ready: ${params.sessionName} · Join now${cycleSuffix}`,
    priority: 'urgent',
    data: {
      sessionId: params.sessionId,
      sessionName: params.sessionName,
      ...(params.cycleId ? { cycleId: params.cycleId } : {}),
      ...(params.cycleName ? { cycleName: params.cycleName } : {}),
    },
    actionUrl: `/prioritization/sessions/${params.sessionId}/participate`,
    actionLabel: 'Join',
    senderId: params.senderId,
  });
}

/**
 * Send cycle phase changed notification
 */
/**
 * In-app (and default-channel) notice when someone becomes track owner for a cycle-bound track.
 */
export async function notifyTrackOwnerAssignment(params: {
  recipientUserId: string;
  trackName: string;
  cycleName: string;
  trackId: string;
  cycleId?: string | null;
}): Promise<NotificationPayload> {
  const body = `You've been made the owner of ${params.trackName} in ${params.cycleName}. You're responsible for configuring participants and running sessions.`;
  return sendNotification({
    event: 'assignment',
    channels: DEFAULT_EVENT_CHANNELS.assignment,
    recipientIds: [params.recipientUserId],
    title: 'Track owner assignment',
    body,
    priority: 'normal',
    data: {
      kind: 'track_owner',
      trackId: params.trackId,
      cycleId: params.cycleId ?? undefined,
      trackName: params.trackName,
      cycleName: params.cycleName,
    },
    actionUrl: `/prioritization/tracks/${params.trackId}`,
    actionLabel: 'Open track',
  });
}

export async function notifyCyclePhaseChanged(
  cycleId: string,
  cycleName: string,
  previousPhase: string,
  newPhase: string,
  recipientIds: string[]
): Promise<NotificationPayload> {
  return sendNotification({
    event: 'cycle_phase_changed',
    channels: DEFAULT_EVENT_CHANNELS.cycle_phase_changed,
    recipientIds,
    title: 'Cycle Phase Updated',
    body: `"${cycleName}" has moved from ${previousPhase} to ${newPhase}`,
    priority: 'normal',
    data: {
      cycleId,
      cycleName,
      previousPhase,
      newPhase,
    },
    actionUrl: `/prioritization/cycles/${cycleId}`,
    actionLabel: 'View Cycle',
  });
}

/**
 * Send meeting scheduled notification
 */
export async function notifyMeetingScheduled(
  meetingId: string,
  meetingTitle: string,
  scheduledStart: Date,
  recipientIds: string[],
  organizerId: string,
  meetingLink?: string
): Promise<NotificationPayload> {
  return sendNotification({
    event: 'meeting_scheduled',
    channels: DEFAULT_EVENT_CHANNELS.meeting_scheduled,
    recipientIds,
    title: 'Meeting Scheduled',
    body: `You've been invited to "${meetingTitle}" on ${formatDateTime(scheduledStart)}`,
    priority: 'normal',
    data: {
      meetingId,
      meetingTitle,
      scheduledStart,
      organizerId,
      meetingLink,
    },
    actionUrl: `/prioritization/meetings/${meetingId}`,
    actionLabel: 'View Details',
  });
}

// ============================================================================
// Utility Functions
// ============================================================================

/**
 * Format date/time for display in notifications
 */
function formatDateTime(date: Date): string {
  return new Intl.DateTimeFormat('en-US', {
    weekday: 'short',
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
    hour12: true,
  }).format(date);
}

/**
 * Get channels for an event based on user preferences
 */
export function getChannelsForEvent(
  event: NotificationEvent,
  userPreferences?: NotificationPreferences
): NotificationChannel[] {
  if (!userPreferences?.enabled) {
    return [];
  }
  
  const customChannels = userPreferences.eventPreferences?.[event];
  if (customChannels) {
    return customChannels;
  }
  
  return DEFAULT_EVENT_CHANNELS[event];
}

/**
 * Clear all notifications (for testing)
 */
export function clearAllNotifications(): void {
  notificationStore = [];
}

// ============================================================================
// Item Notification Helpers
// ============================================================================

/**
 * Notify a user they have been assigned as item owner (shaping owner)
 */
export async function notifyStewardAssigned(params: {
  itemId: string;
  itemTitle: string;
  stewardId: string;  // represents the assigned item owner (maps to shapingOwnerId)
  stewardName: string;
  workspaceId?: string;
}): Promise<void> {
  try {
    await sendNotification({
      event: 'steward_assigned',
      channels: DEFAULT_EVENT_CHANNELS.steward_assigned,
      recipientIds: [params.stewardId],
      title: "You've been assigned as item owner",
      body: `You are now the item owner for "${params.itemTitle}"`,
      priority: 'normal',
      data: {
        itemId: params.itemId,
        itemTitle: params.itemTitle,
        workspaceId: params.workspaceId,
      },
      actionUrl: `/prioritization/items/${params.itemId}`,
      actionLabel: 'View Item',
    });
  } catch {
    // Silent failure — notifications are non-blocking
  }
}

/**
 * Notify recipients that an item's state has changed
 */
export async function notifyItemStateChanged(params: {
  itemId: string;
  itemTitle: string;
  fromState: string;
  toState: string;
  recipientUserIds: string[];
  workspaceId?: string;
}): Promise<void> {
  try {
    await sendNotification({
      event: 'item_state_changed',
      channels: DEFAULT_EVENT_CHANNELS.item_state_changed,
      recipientIds: params.recipientUserIds,
      title: 'Item status updated',
      body: `"${params.itemTitle}" moved from ${params.fromState} → ${params.toState}`,
      priority: 'normal',
      data: {
        itemId: params.itemId,
        itemTitle: params.itemTitle,
        fromState: params.fromState,
        toState: params.toState,
        workspaceId: params.workspaceId,
      },
      actionUrl: `/prioritization/items/${params.itemId}`,
      actionLabel: 'View Item',
    });
  } catch {
    // Silent failure — notifications are non-blocking
  }
}

/**
 * Notify recipients that an item has met all readiness criteria
 */
export async function notifyItemReadyForRanking(params: {
  itemId: string;
  itemTitle: string;
  recipientUserIds: string[];
  workspaceId?: string;
}): Promise<void> {
  try {
    await sendNotification({
      event: 'item_ready_for_ranking',
      channels: DEFAULT_EVENT_CHANNELS.item_ready_for_ranking,
      recipientIds: params.recipientUserIds,
      title: 'Item ready for ranking',
      body: `"${params.itemTitle}" has met all readiness criteria`,
      priority: 'normal',
      data: {
        itemId: params.itemId,
        itemTitle: params.itemTitle,
        workspaceId: params.workspaceId,
      },
      actionUrl: `/prioritization/items/${params.itemId}`,
      actionLabel: 'View Item',
    });
  } catch {
    // Silent failure — notifications are non-blocking
  }
}

/**
 * Notify recipients that an item has been deprioritized via the zombie cleanup tool.
 * Gives the owner context to decide whether to complete or cease active work.
 */
export async function notifyItemDeprioritized(params: {
  itemId: string;
  itemTitle: string;
  recipientUserIds: string[];
  action: 'archived' | 'deferred';
  actorId: string;
  stalenessThresholdDays: number;
  deferReviewDate?: Date;
  workspaceId?: string;
}): Promise<void> {
  try {
    const isArchived = params.action === 'archived';
    const dateStr = params.deferReviewDate
      ? new Intl.DateTimeFormat('en-US', { month: 'short', day: 'numeric', year: 'numeric' }).format(params.deferReviewDate)
      : null;

    const body = isArchived
      ? `"${params.itemTitle}" was identified as a zombie priority — it has had no activity in ${params.stalenessThresholdDays}+ days and has not been part of any active cycle. It has been archived. If your team has work in progress on this item, consider wrapping it up or formally pausing it before closing out.`
      : `"${params.itemTitle}" was identified as a zombie priority and has been deferred${dateStr ? ` for review on ${dateStr}` : ''}. If your team has active work on this item, consider wrapping it up before the review date or noting the current state for when it is revisited.`;

    await sendNotification({
      event: 'item_deprioritized',
      channels: DEFAULT_EVENT_CHANNELS.item_deprioritized,
      recipientIds: params.recipientUserIds,
      title: isArchived
        ? `"${params.itemTitle}" removed from active priorities`
        : `"${params.itemTitle}" deferred — review your active work`,
      body,
      priority: 'normal',
      data: {
        itemId: params.itemId,
        itemTitle: params.itemTitle,
        action: params.action,
        actorId: params.actorId,
        stalenessThresholdDays: params.stalenessThresholdDays,
        deferReviewDate: params.deferReviewDate?.toISOString(),
        workspaceId: params.workspaceId,
      },
      actionUrl: `/prioritization/items/${params.itemId}`,
      actionLabel: 'View Item',
    });
  } catch {
    // Silent failure — notifications are non-blocking
  }
}

export default {
  sendNotification,
  getNotificationsForUser,
  getUnreadCount,
  markAsRead,
  markAllNotificationsReadInSupabase,
  dismissNotificationInSupabase,
  subscribeToNotifications,
  notifySessionScheduled,
  notifySessionStartingSoon,
  notifySessionLiveNow,
  loadSessionJoinability,
  notifyCyclePhaseChanged,
  notifyMeetingScheduled,
  notifyStewardAssigned,
  notifyItemStateChanged,
  notifyItemReadyForRanking,
  notifyItemDeprioritized,
  getChannelsForEvent,
  DEFAULT_EVENT_CHANNELS,
};
